import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  X,
  Download,
  FileText,
  File,
  Image as ImageIcon,
  Archive,
  Calendar,
  User,
  ExternalLink,
  CheckSquare,
  Square
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';


const API = "/api";

const AttachmentsModal = ({ isOpen, onClose, productId, productTitle }) => {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedAttachments, setSelectedAttachments] = useState(new Set());
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  const { toast } = useToast();

  const getFileIcon = (fileName) => {
    if (!fileName) return File;
    const extension = fileName.toLowerCase().split('.').pop();
    
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'].includes(extension)) {
      return ImageIcon;
    } else if (['pdf', 'doc', 'docx', 'txt', 'rtf'].includes(extension)) {
      return FileText;
    } else if (['zip', 'rar', '7z', 'tar', 'gz'].includes(extension)) {
      return Archive;
    } else {
      return File;
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour12: false
    });
  };

  const fetchAttachments = async () => {
    if (!productId) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/product-attachments/${productId}`);
      setAttachments(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch attachments",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && productId) {
      fetchAttachments();
    }
  }, [isOpen, productId]);

  const handleDownload = (attachment) => {
    if (attachment.file_url) {
      const fullUrl = `/api${attachment.file_url}`;
      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = fullUrl;
      link.download = attachment.file_name || 'download';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const toggleMultiSelectMode = () => {
    setIsMultiSelectMode(!isMultiSelectMode);
    setSelectedAttachments(new Set());
  };

  const toggleAttachmentSelection = (attachmentId) => {
    const newSelection = new Set(selectedAttachments);
    if (newSelection.has(attachmentId)) {
      newSelection.delete(attachmentId);
    } else {
      newSelection.add(attachmentId);
    }
    setSelectedAttachments(newSelection);
  };

  const handleDownloadSelected = async () => {
    if (selectedAttachments.size === 0) {
      toast({
        title: "No files selected",
        description: "Please select at least one file to download",
        variant: "destructive",
      });
      return;
    }

    try {
      const response = await axios.post(
        `${API}/product-notes/download-multiple`,
        { note_ids: Array.from(selectedAttachments) },
        {
          responseType: 'blob',
        }
      );

      // Create download link for zip file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `attachments_${selectedAttachments.size}_files.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({
        title: "Success",
        description: `Downloaded ${selectedAttachments.size} files as ZIP`,
      });

      // Clear selection after download
      setSelectedAttachments(new Set());
      setIsMultiSelectMode(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to download selected files",
        variant: "destructive",
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">
              Attachments
            </h2>
            <p className="text-slate-600 mt-1">
              All files attached to "{productTitle}"
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="flex items-center gap-2"
          >
            <X className="w-4 h-4" />
            Close
          </Button>
        </div>

        {/* Multi-select Controls */}
        {attachments.length > 0 && (
          <div className="px-6 py-3 border-b border-slate-200 bg-slate-50">
            <div className="flex items-center justify-between">
              <Button
                variant={isMultiSelectMode ? "default" : "outline"}
                size="sm"
                onClick={toggleMultiSelectMode}
                className="flex items-center gap-2"
              >
                {isMultiSelectMode ? (
                  <CheckSquare className="w-4 h-4" />
                ) : (
                  <Square className="w-4 h-4" />
                )}
                {isMultiSelectMode ? `Selecting (${selectedAttachments.size})` : 'Multi-Select'}
              </Button>

              {isMultiSelectMode && selectedAttachments.size > 0 && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleDownloadSelected}
                  className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
                >
                  <Download className="w-4 h-4" />
                  Download Selected ({selectedAttachments.size})
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="spinner w-8 h-8 mx-auto mb-4"></div>
                <p className="text-slate-600">Loading attachments...</p>
              </div>
            </div>
          ) : attachments.length === 0 ? (
            <div className="text-center py-12">
              <File className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                No attachments yet
              </h3>
              <p className="text-slate-600">
                Files uploaded to the timeline will appear here.
              </p>
            </div>
          ) : (
            <div className="space-y-4 max-h-[50vh] overflow-y-auto">
              {attachments.map((attachment) => {
                const FileIcon = getFileIcon(attachment.file_name);
                return (
                  <div
                    key={attachment.id}
                    className={`flex items-center justify-between p-4 rounded-lg transition-colors ${
                      isMultiSelectMode && selectedAttachments.has(attachment.id)
                        ? 'bg-blue-100 border-2 border-blue-300'
                        : 'bg-slate-50 hover:bg-slate-100'
                    }`}
                  >
                    <div className="flex items-center space-x-4 flex-1 min-w-0">
                      {isMultiSelectMode && (
                        <button
                          onClick={() => toggleAttachmentSelection(attachment.id)}
                          className="flex-shrink-0 p-1"
                        >
                          {selectedAttachments.has(attachment.id) ? (
                            <CheckSquare className="w-5 h-5 text-blue-600" />
                          ) : (
                            <Square className="w-5 h-5 text-slate-400" />
                          )}
                        </button>
                      )}
                      <div className="flex-shrink-0">
                        <FileIcon className="w-8 h-8 text-slate-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1">
                          <h4 className="font-medium text-slate-900 truncate">
                            {attachment.file_name || 'Unknown file'}
                          </h4>
                          {attachment.file_size && (
                            <Badge variant="outline" className="text-xs">
                              {formatFileSize(attachment.file_size)}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-slate-500">
                          <div className="flex items-center space-x-1">
                            <User className="w-3 h-3" />
                            <span>{attachment.user_name}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Calendar className="w-3 h-3" />
                            <span>{formatDateTime(attachment.created_at)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 flex-shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownload(attachment)}
                        className="flex items-center space-x-1"
                      >
                        <Download className="w-4 h-4" />
                        <span className="hidden sm:inline">Download</span>
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 px-6 py-4 bg-slate-50 rounded-b-xl">
          <div className="flex items-center justify-between">
            <div className="text-sm text-slate-600">
              {attachments.length} attachment{attachments.length !== 1 ? 's' : ''} total
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={onClose}
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttachmentsModal;